import { TRIAL_CREDIT_GRANT } from '@lensora/shared'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
  cn,
} from '@lensora/ui'
import * as React from 'react'
import { useSearchParams, Link } from 'react-router-dom'

import { ResendConfirmationButton } from '@/components/auth/ResendConfirmationButton'
import { GoogleIcon } from '@/components/google-icon'
import { stashPendingWebsite } from '@/components/onboarding/persistence'
import { api, ApiError } from '@/lib/api'
import { authClient } from '@/lib/auth'
import { type PlanTier } from '@/lib/billing'

const PLANS: {
  id: PlanTier
  name: string
  price: string
  description: string
  recommended?: boolean
  features: string[]
}[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: '£49/mo',
    description: 'Track up to 3 websites with monthly reports',
    features: [
      '500 credits/mo',
      '1 website',
      'ChatGPT, Claude, Gemini, Perplexity',
      'Competitor tracking',
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    price: '£149/mo',
    description: 'Unlimited websites with weekly reports and insights',
    recommended: true,
    features: [
      '2,500 credits/mo',
      'Up to 5 websites',
      'Everything in Starter',
      'Advanced insights',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Contact us',
    description: 'For large organisations with custom needs',
    features: [
      'Unlimited credits',
      'Unlimited websites',
      'White-label reports',
      'Dedicated account manager',
    ],
  },
]

function parseTier(value: string | null): PlanTier | null {
  return value === 'starter' || value === 'growth' || value === 'enterprise' ? value : null
}

export function SignupPage() {
  const [searchParams] = useSearchParams()
  // The marketing pricing page deep-links /signup?plan=<tier>. The billing
  // interval is no longer read here — it is chosen at checkout, which now
  // happens from the billing page rather than immediately after signup.
  const [plan, setPlan] = React.useState<PlanTier>(
    () => parseTier(searchParams.get('plan')) ?? 'growth'
  )
  const [fullName, setFullName] = React.useState('')
  const [companyName, setCompanyName] = React.useState('')
  // The marketing hero deep-links /signup?email=<addr>&website=<url> —
  // email prefills here (still editable), website is stashed for the
  // onboarding wizard's step 2 (see stashPendingWebsite below).
  const [email, setEmail] = React.useState(() => searchParams.get('email') ?? '')
  const [password, setPassword] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = React.useState(false)
  const [confirmationSent, setConfirmationSent] = React.useState(false)

  // Stash the website param immediately — before any OAuth redirect (which
  // loses in-memory state) or plain account-creation navigation.
  React.useEffect(() => {
    const website = searchParams.get('website')
    if (website) stashPendingWebsite(website)
  }, [searchParams])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      await api.post('/api/auth/signup', { email, password, fullName, companyName, planTier: plan })
      // Trial credits are now gated on confirming the emailed link, so there
      // is nothing useful to sign in to yet — always land on the
      // check-your-inbox screen instead of attempting a sign-in.
      setConfirmationSent(true)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          err.code === 'EMAIL_TAKEN'
            ? 'An account with this email already exists. Try signing in.'
            : err.message
        )
      } else {
        setError('Something went wrong. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  async function handleGoogleSignUp() {
    setError(null)
    setIsGoogleLoading(true)
    try {
      // The completion page collects the company name and creates the profile.
      // The plan choice deliberately isn't carried across the redirect any
      // more — no checkout is forced, so there is nothing to resume.
      await authClient.signInWithOAuth('google', `${window.location.origin}/signup/complete`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign up failed')
      setIsGoogleLoading(false)
    }
  }

  if (confirmationSent) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-raised p-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center">
            <div className="mx-auto mb-4 h-10 w-10 rounded-lg bg-brand-primary" />
          </div>
          <Card>
            <CardContent className="pt-8 pb-8 text-center space-y-4">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-feedback-success/10">
                <svg
                  className="h-7 w-7 text-feedback-success"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-text mb-1">Check your inbox</h2>
                <p className="text-sm text-text-muted">
                  We sent a confirmation link to{' '}
                  <span className="text-text font-medium">{email}</span>. Confirm your email to
                  activate your {TRIAL_CREDIT_GRANT} free credits, then{' '}
                  <Link to="/login" className="text-brand-accent hover:underline">
                    sign in here
                  </Link>
                  .
                </p>
              </div>
              <p className="text-xs text-text-muted">
                Didn't receive it? Check your spam folder, or resend below.
              </p>
              <ResendConfirmationButton email={email} />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-raised p-4">
      <div className="w-full max-w-lg space-y-6">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 rounded-lg bg-brand-primary" />
          <h1 className="text-2xl font-bold text-text">Get started with Lensora</h1>
          <p className="mt-1 text-sm text-text-muted">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-accent hover:underline">
              Sign in
            </Link>
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <CardTitle className="text-base">Create your account</CardTitle>
              <span className="inline-flex items-center rounded-full border border-brand-accent/30 bg-brand-accent/10 px-2.5 py-0.5 text-xs font-medium text-brand-accent">
                No card required
              </span>
            </div>
            <CardDescription>
              Enterprise?{' '}
              <a href="mailto:hello@lensora.io" className="text-brand-accent hover:underline">
                Contact us
              </a>{' '}
              for a custom quote.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Plan selection */}
            <div className="grid gap-3">
              {PLANS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlan(p.id)}
                  className={cn(
                    'relative rounded-lg border-2 p-4 text-left transition-colors',
                    plan === p.id
                      ? 'border-brand-accent bg-brand-accent/5'
                      : 'border-border hover:border-brand-accent/40'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-text">{p.name}</p>
                      {p.recommended && (
                        <span className="inline-flex items-center rounded-full bg-brand-accent/10 px-2 py-0.5 text-xs font-medium text-brand-accent">
                          Recommended
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-brand-accent">{p.price}</p>
                  </div>
                  <p className="mt-0.5 text-xs text-text-muted">{p.description}</p>
                  {plan === p.id && (
                    <ul className="mt-2 space-y-1">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-center gap-1.5 text-xs text-text-muted">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M2 6l3 3 5-5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-brand-accent"
                            />
                          </svg>
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                </button>
              ))}
            </div>

            {plan !== 'enterprise' && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleSignUp}
                  disabled={isGoogleLoading || isLoading}
                >
                  <GoogleIcon />
                  {isGoogleLoading ? 'Connecting…' : 'Continue with Google'}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-surface px-2 text-text-muted">or sign up with email</span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="full-name">Full name</Label>
                      <Input
                        id="full-name"
                        placeholder="Jane Smith"
                        autoComplete="name"
                        required
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company-name">Company name</Label>
                      <Input
                        id="company-name"
                        placeholder="Acme Ltd"
                        required
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Work email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Min. 8 characters"
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>

                  <div className="rounded-lg border border-feedback-success/20 bg-feedback-success/5 px-4 py-3">
                    <p className="text-sm font-medium text-feedback-success">
                      {TRIAL_CREDIT_GRANT} free credits — no card required
                    </p>
                    <p className="mt-0.5 text-xs text-feedback-success/70">
                      Confirm your email to activate them, then scan your brand across every AI
                      platform. Add a payment method only when you want to continue.
                    </p>
                  </div>

                  {error && <p className="text-sm text-feedback-error">{error}</p>}
                  <Button type="submit" className="w-full" disabled={isLoading || isGoogleLoading}>
                    {isLoading ? 'Creating account…' : 'Create account'}
                  </Button>
                  <p className="text-center text-xs text-text-muted">
                    By signing up you agree to our{' '}
                    <a
                      href="https://lensora.io/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://lensora.io/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </form>
              </>
            )}

            {plan === 'enterprise' && (
              <div className="rounded-lg border border-border bg-surface-raised p-6 text-center space-y-3">
                <p className="font-medium text-text">Let's talk</p>
                <p className="text-sm text-text-muted">
                  Enterprise plans are tailored to your needs. Get in touch and we'll set you up
                  with a custom quote.
                </p>
                <Button asChild>
                  <a href="mailto:hello@lensora.io">Contact sales</a>
                </Button>
              </div>
            )}
          </CardContent>
          <CardFooter className="justify-center">
            <p className="text-sm text-text-muted">
              Already have an account?{' '}
              <Link to="/login" className="text-brand-accent hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  )
}
